import {
  CTCSS_TONES,
  DTCS_CODES,
  decodeLbcd,
  encodeLbcd,
  type RadioProgress,
  type WebChannel,
} from './baofeng-web.ts'
import type { RadioTransport } from './web-serial.ts'

const ACK = 0x06
const MEMORY_SIZE = 0x8000
const BLOCK_SIZE = 256
const CHANNEL_START = 0x2000
const CHANNEL_SIZE = 48
const CHANNEL_COUNT = 250
const SCAN_START = 0x0061

type TKx80Spec = {
  id: string
  model: string
  type: string
  summary: string
  powerLevels: readonly string[]
}

export type TKx80Profile = {
  protocol: 'kenwood-tkx80'
  id: string
  vendor: 'Kenwood'
  model: string
  summary: string
  tags: readonly string[]
  baudRate: 9600
  parity: 'even'
  channelMinimum: 1
  channelMaximum: 250
  nameLength: 10
  memorySize: number
  types: readonly string[]
  powerLevels: readonly string[]
}

const SPECS: readonly TKx80Spec[] = [
  { id: 'kenwood-tk-280', model: 'TK-280', type: 'P0280', summary: 'Kenwood TK-280 VHF portable.', powerLevels: ['Low', 'High'] },
  { id: 'kenwood-tk-380', model: 'TK-380', type: 'P0380', summary: 'Kenwood TK-380 UHF portable.', powerLevels: ['Low', 'High'] },
  { id: 'kenwood-tk-780', model: 'TK-780', type: 'M0780', summary: 'Kenwood TK-780 VHF mobile.', powerLevels: ['Low', 'High'] },
  { id: 'kenwood-tk-880', model: 'TK-880', type: 'M0880', summary: 'Kenwood TK-880 UHF mobile.', powerLevels: ['Low', 'High'] },
]

export const TKX80_PROFILES: readonly TKx80Profile[] = SPECS.map((spec) => ({
  protocol: 'kenwood-tkx80',
  id: spec.id,
  vendor: 'Kenwood',
  model: spec.model,
  summary: spec.summary,
  tags: ['Experimental', 'Read + write', '250 physical slots', '10-character names'],
  baudRate: 9600,
  parity: 'even',
  channelMinimum: 1,
  channelMaximum: CHANNEL_COUNT,
  nameLength: 10,
  memorySize: MEMORY_SIZE,
  types: [spec.type],
  powerLevels: spec.powerLevels,
}))

function emptyChannel(number: number, power: string): WebChannel {
  return {
    number,
    empty: true,
    name: '',
    rxFrequency: null,
    txFrequency: null,
    toneMode: 'none',
    ctcss: null,
    dtcs: null,
    dtcsPolarity: 'N',
    mode: 'NFM',
    power,
    skip: false,
  }
}

type DecodedTone = {
  mode: 'none' | 'ctcss' | 'dtcs'
  ctcss: number | null
  dtcs: number | null
  polarity: 'N' | 'R'
}

function decodeTone(raw: Uint8Array): DecodedTone {
  const value = raw[0] | (raw[1] << 8)
  if (value === 0xffff) return { mode: 'none', ctcss: null, dtcs: null, polarity: 'N' }
  if (value >= 0x2800) {
    return {
      mode: 'dtcs',
      ctcss: null,
      dtcs: Number((value & 0x07ff).toString(8)),
      polarity: value & 0x8000 ? 'R' : 'N',
    }
  }
  return { mode: 'ctcss', ctcss: value / 10, dtcs: null, polarity: 'N' }
}

function encodeTone(channel: WebChannel): readonly [number, number] {
  let value = 0xffff
  if (channel.toneMode === 'ctcss' && channel.ctcss) {
    value = Math.round(channel.ctcss * 10)
  } else if (channel.toneMode === 'dtcs' && channel.dtcs) {
    value = Number.parseInt(channel.dtcs.toString(), 8) | 0x2800
    if (channel.dtcsPolarity === 'R') value |= 0xa000
  }
  return [value & 0xff, (value >> 8) & 0xff]
}

function toneMatches(channel: WebChannel, tone: DecodedTone): boolean {
  if (channel.toneMode === 'none') return tone.mode === 'none'
  if (channel.toneMode === 'ctcss') return tone.mode === 'ctcss' && tone.ctcss === channel.ctcss
  return tone.mode === 'dtcs' && tone.dtcs === channel.dtcs && tone.polarity === channel.dtcsPolarity
}

function decodeName(raw: Uint8Array): string {
  return String.fromCharCode(
    ...raw.map((value) => (value === 0 || value === 0xff ? 0x20 : value)),
  ).trim()
}

function scanBit(image: Uint8Array, number: number): boolean {
  const index = number - 1
  return (image[SCAN_START + Math.floor(index / 8)] & (1 << (index & 7))) !== 0
}

function setScanBit(image: Uint8Array, number: number, value: boolean): void {
  const index = number - 1
  const offset = SCAN_START + Math.floor(index / 8)
  const bit = 1 << (index & 7)
  image[offset] = value ? image[offset] | bit : image[offset] & ~bit
}

export function parseTKx80Channel(
  image: Uint8Array,
  profile: TKx80Profile,
  number: number,
): WebChannel {
  const offset = CHANNEL_START + (number - 1) * CHANNEL_SIZE
  const raw = image.slice(offset, offset + CHANNEL_SIZE)
  if (raw.byteLength !== CHANNEL_SIZE || raw[0] === 0xff) return emptyChannel(number, profile.powerLevels[0])
  const rxFrequency = decodeLbcd(raw.slice(12, 16))
  if (!rxFrequency) return emptyChannel(number, profile.powerLevels[0])
  const txFrequency = raw[16] === 0xff ? null : decodeLbcd(raw.slice(16, 20))
  const txTone = decodeTone(raw.slice(24, 26))
  const rxtone = decodeTone(raw.slice(22, 24))
  const selected = txTone.mode === 'none' ? rxtone : txTone
  return {
    number,
    empty: false,
    name: decodeName(raw.slice(2, 12)),
    rxFrequency,
    txFrequency,
    toneMode: selected.mode,
    ctcss: selected.ctcss,
    dtcs: selected.dtcs,
    dtcsPolarity: selected.polarity,
    mode: raw[32] & 0x80 ? 'FM' : 'NFM',
    power: raw[32] & 0x20 ? profile.powerLevels[1] : profile.powerLevels[0],
    skip: !scanBit(image, number),
  }
}

export function parseTKx80Channels(image: Uint8Array, profile: TKx80Profile): WebChannel[] {
  return Array.from({ length: 250 }, (_, index) => parseTKx80Channel(image, profile, index + 1))
}

export function applyTKx80Channel(image: Uint8Array, profile: TKx80Profile, channel: WebChannel): void {
  const offset = CHANNEL_START + (channel.number - 1) * CHANNEL_SIZE
  const raw = image.subarray(offset, offset + CHANNEL_SIZE)
  if (channel.empty) {
    raw.fill(0xff)
    setScanBit(image, channel.number, false)
    return
  }
  if (!channel.rxFrequency) throw new Error('A receive frequency is required.')
  raw.fill(0)
  raw[0] = channel.number
  const name = channel.name.slice(0, 10).padEnd(10, ' ')
  for (let index = 0; index < 10; index += 1) raw[2 + index] = name.charCodeAt(index)
  encodeLbcd(channel.rxFrequency, raw.subarray(12, 16))
  if (channel.txFrequency) encodeLbcd(channel.txFrequency, raw.subarray(16, 20))
  else raw.subarray(16, 20).fill(0xff)
  const tone = encodeTone(channel)
  raw.set(tone, 22)
  raw.set(tone, 24)
  raw[32] =
    (channel.power === profile.powerLevels[1] ? 0x20 : 0) |
    (channel.mode === 'FM' ? 0x80 : 0)
  setScanBit(image, channel.number, !channel.skip)
}

function frame(command: number, block: number): Uint8Array {
  return Uint8Array.of(command, (block >> 8) & 0xff, block & 0xff)
}

async function exchangeAck(transport: RadioTransport): Promise<void> {
  await transport.write(Uint8Array.of(ACK))
  if ((await transport.readExact(1, 2500))[0] !== ACK) throw new Error('The Kenwood radio rejected the block exchange.')
}

async function enter(profile: TKx80Profile, transport: RadioTransport): Promise<void> {
  await transport.discardInput()
  let entered = false
  for (let attempt = 0; attempt < 10 && !entered; attempt += 1) {
    await transport.write(new TextEncoder().encode('PROGRAM'))
    try {
      if ((await transport.readExact(1, 2500))[0] !== ACK) {
        throw new Error('The radio rejected PROGRAM.')
      }
      entered = true
    } catch {
      // Older Kenwood firmware may require several PROGRAM attempts.
    }
  }
  if (!entered) throw new Error('The radio refused TK-x80 programming mode.')
  await transport.write(Uint8Array.of(0x02))
  const identity = await transport.readExact(8, 3000)
  if (!profile.types.some((type) => new TextDecoder().decode(identity).includes(type))) {
    throw new Error(`The radio does not identify as ${profile.model}.`)
  }
  await exchangeAck(transport)
  await transport.write(new TextEncoder().encode('P'))
  await transport.readExact(10, 3000)
  await exchangeAck(transport)
  await transport.write(new TextEncoder().encode('O'))
}

export async function readTKx80RadioWithTransport(
  profile: TKx80Profile,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<{ image: Uint8Array; reportedModel: string }> {
  await enter(profile, transport)
  try {
    const image = new Uint8Array(MEMORY_SIZE)
    const total = MEMORY_SIZE / BLOCK_SIZE
    for (let block = 0; block < total; block += 1) {
      await transport.write(frame(0x52, block))
      const marker = (await transport.readExact(1, 4000))[0]
      if (marker === 0x57) image.set(await transport.readExact(256, 5000), block * 256)
      else if (marker === 0x5a) image.fill(0xff, block * 256, (block + 1) * 256)
      else throw new Error(`Unexpected TK-x80 marker at block ${block}.`)
      await transport.readExact(1, 2000)
      await exchangeAck(transport)
      onProgress?.({ current: block + 1, total, label: 'Reading Kenwood TK-x80 memory' })
    }
    return { image, reportedModel: profile.model }
  } finally {
    await transport.write(new TextEncoder().encode('E'))
  }
}

export async function writeTKx80RadioWithTransport(
  profile: TKx80Profile,
  image: Uint8Array,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<void> {
  await enter(profile, transport)
  try {
    const total = MEMORY_SIZE / BLOCK_SIZE
    for (let block = 0; block < total; block += 1) {
      if (block > 0x50 && block < 0x60) continue
      const chunk = image.slice(block * 256, (block + 1) * 256)
      if (chunk.every((value) => value === 0xff)) {
        await transport.write(frame(0x5a, block))
      } else {
        let sum = 0
        for (const value of chunk) sum = (sum + value) & 0xff
        const packet = new Uint8Array(4 + 256)
        packet.set(frame(0x57, block))
        packet.set(chunk, 3)
        packet[259] = sum
        await transport.write(packet)
      }
      await exchangeAck(transport)
      onProgress?.({ current: block + 1, total, label: 'Writing Kenwood TK-x80 memory' })
    }
  } finally {
    await transport.write(new TextEncoder().encode('E'))
  }
}
